import { formatSiteDate, formatSiteDateTime } from "@/lib/site-time";
/* i18n-ignore: interne adminpagina, alleen NL */
import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Eye, History, Languages, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { RichText } from "@/components/site/RichText";
import { Skeleton } from "@/components/ui/skeleton";
import { AdminNav } from "@/components/admin/AdminNav";
import { MediaPicker } from "@/components/admin/MediaPicker";
import { MediaDialog } from "@/components/admin/MediaDialog";
import { adminPageQuery, revisionsQuery, translationStatusQuery } from "@/lib/content-queries";
import { restoreRevision, savePage, type ContentSectionInput, type SectionMedia } from "@/lib/content.functions";
import { suggestPageTranslation, type VertaalSectie, type VertaalVoorstel } from "@/lib/ai-translate.functions";
import { LANG_LABELS, LANGS, type Lang } from "@/i18n/config";

export const Route = createFileRoute("/_authenticated/admin/paginas/$sleutel")({
  staticData: { sitemap: false },
  head: () => ({ meta: [{ title: "Pagina bewerken — Gopro Projects" }, { name: "robots", content: "noindex" }] }),
  component: AdminPageEditor,
});

type FormState = {
  metaTitle: string;
  metaDescription: string;
  ogMediaId: string | null;
  indexeerbaar: boolean;
  sections: ContentSectionInput[];
};

const LEEG: FormState = { metaTitle: "", metaDescription: "", ogMediaId: null, indexeerbaar: true, sections: [] };

function datum(waarde: string) {
  return waarde ? formatSiteDate(waarde) : "";
}

function AdminPageEditor() {
  const { sleutel } = Route.useParams();
  const queryClient = useQueryClient();
  const [taal, setTaal] = useState<Lang>("nl");
  const { data, isLoading, isError } = useQuery(adminPageQuery(sleutel, taal));
  const nederlands = useQuery({ ...adminPageQuery(sleutel, "nl"), enabled: taal !== "nl" });
  const vertaalstand = useQuery(translationStatusQuery(sleutel));
  const [form, setForm] = useState<FormState>(LEEG);
  const [status, setStatus] = useState<"concept" | "gepubliceerd">("concept");
  const [voorstel, setVoorstel] = useState<VertaalVoorstel | null>(null);
  const [waarschuwingen, setWaarschuwingen] = useState<string[]>([]);
  const revisions = useQuery(revisionsQuery(data?.id ?? ""));

  const bron = taal === "nl" ? data : (nederlands.data ?? null);
  const stand = (vertaalstand.data ?? []).find((rij) => rij.lang === taal) ?? null;

  useEffect(() => {
    setVoorstel(null);
    setWaarschuwingen([]);
  }, [taal]);

  useEffect(() => {
    if (taal === "nl") {
      if (!data) return;
      setStatus(data.status);
      setForm(naarForm(data));
      return;
    }
    if (!bron) return;
    const bestaand = new Map((data?.sections ?? []).map((section) => [section.sleutel, section]));
    setStatus(data?.status ?? "concept");
    setForm({
      metaTitle: data?.metaTitle ?? "",
      metaDescription: data?.metaDescription ?? "",
      ogMediaId: data?.ogMediaId ?? null,
      indexeerbaar: data?.indexeerbaar ?? true,
      sections: bron.sections.map((section) => {
        const eigen = bestaand.get(section.sleutel);
        return {
          ...(eigen?.id ? { id: eigen.id } : {}),
          sleutel: section.sleutel,
          sortOrder: section.sortOrder,
          type: section.type,
          overline: eigen?.overline ?? "",
          titel: eigen?.titel ?? "",
          tekst: eigen?.tekst ?? "",
          opsomming: eigen?.opsomming ?? [],
          knopTekst: eigen?.knopTekst ?? "",
          knopLink: eigen?.knopLink ?? section.knopLink,
          knop2Tekst: eigen?.knop2Tekst ?? "",
          knop2Link: eigen?.knop2Link ?? section.knop2Link,
          mediaId: eigen?.mediaId ?? section.mediaId,
          alt: eigen?.alt ?? "",
          media: eigen?.media?.length ? eigen.media : section.media,
        } satisfies ContentSectionInput;
      }),
    });
  }, [data, bron, taal]);

  const save = useMutation({
    mutationFn: (nieuweStatus: "concept" | "gepubliceerd") =>
      savePage({ data: { sleutel, lang: taal, status: nieuweStatus, ...form } }),
    onSuccess: (_result, nieuweStatus) => {
      setStatus(nieuweStatus);
      toast.success(nieuweStatus === "gepubliceerd" ? "Pagina gepubliceerd." : "Opgeslagen als concept.");
      void queryClient.invalidateQueries({ queryKey: ["content"] });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Opslaan is niet gelukt."),
  });

  const vertaal = useMutation({
    mutationFn: () => suggestPageTranslation({ data: { sleutel, doel: taal === "fr" ? "fr" : "en" } }),
    onSuccess: (resultaat) => {
      setVoorstel(resultaat.voorstel);
      setWaarschuwingen(resultaat.waarschuwingen);
      toast.success(`Vertaalvoorstel klaar. Vandaag nog ${resultaat.restantVandaag} keer mogelijk.`);
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "De vertaling is niet gelukt."),
  });

  const restore = useMutation({
    mutationFn: (revisionId: string) => restoreRevision({ data: { revisionId } }),
    onSuccess: () => {
      toast.success("Deze versie staat opnieuw klaar als concept.");
      void queryClient.invalidateQueries({ queryKey: ["content"] });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Terugzetten is niet gelukt."),
  });

  function update(index: number, patch: Partial<ContentSectionInput>) {
    setForm((prev) => ({
      ...prev,
      sections: prev.sections.map((section, i) => (i === index ? { ...section, ...patch } : section)),
    }));
  }

  const voorstelPerSectie = useMemo(() => {
    const map = new Map<string, VertaalSectie>();
    for (const sectie of voorstel?.secties ?? []) map.set(sectie.sleutel, sectie);
    return map;
  }, [voorstel]);

  if (isLoading) return <Skeleton className="m-8 h-96 rounded-xl" />;

  if (isError) {
    return (
      <main className="mx-auto w-full max-w-4xl px-4 py-10">
        <AdminNav />
        <p className="text-sm text-destructive">Deze pagina kon niet geladen worden. Probeer het later opnieuw.</p>
      </main>
    );
  }

  const heroIndex = form.sections.findIndex((section) => section.type === "hero");
  const overige = form.sections.filter((_, index) => index !== heroIndex);
  const bronSecties = new Map((bron?.sections ?? []).map((section) => [section.sleutel, section]));

  return (
    <main className="mx-auto w-full max-w-4xl px-4 py-10 sm:px-6">
      <AdminNav />
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <Button asChild variant="ghost" size="sm" className="-ml-2 mb-1">
            <Link to="/admin/paginas">
              <ArrowLeft className="mr-1 h-4 w-4" /> Alle pagina's
            </Link>
          </Button>
          <h1 className="text-2xl font-extrabold tracking-tight">{sleutel}</h1>
          <p className="text-sm text-muted-foreground">
            {LANG_LABELS[taal]} — status:{" "}
            {status === "gepubliceerd"
              ? "online, de website toont deze inhoud"
              : "concept, de website toont de Nederlandse tekst of de tekst uit de code"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline" size="sm">
            <a
              href={`/${taal}${sleutel === "home" ? "/" : `/${sleutel}`}?voorvertoning=1`}
              target="_blank"
              rel="noreferrer"
            >
              <Eye className="mr-1 h-4 w-4" /> Voorvertoning
            </a>
          </Button>
          <Button variant="outline" size="sm" disabled={save.isPending} onClick={() => save.mutate("concept")}>
            {save.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
            Bewaren als concept
          </Button>
          <Button size="sm" disabled={save.isPending} onClick={() => save.mutate("gepubliceerd")}>
            Publiceren
          </Button>
        </div>
      </div>

      <Card className="mb-6">
        <CardContent className="space-y-3 p-5">
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Taal kiezen">
            <span className="text-sm font-semibold">Taal</span>
            {LANGS.map((code) => (
              <Button
                key={code}
                type="button"
                size="sm"
                variant={taal === code ? "default" : "outline"}
                aria-pressed={taal === code}
                onClick={() => setTaal(code)}
              >
                {LANG_LABELS[code]}
              </Button>
            ))}
            {taal !== "nl" && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={vertaal.isPending}
                onClick={() => vertaal.mutate()}
              >
                {vertaal.isPending ? (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                ) : (
                  <Languages className="mr-1 h-4 w-4" />
                )}
                Vertaalvoorstel maken
              </Button>
            )}
          </div>

          {taal !== "nl" && stand && (
            <div className="space-y-1 border-t pt-3 text-sm">
              <p>
                {stand.vertaald} van de {stand.totaal} blokken zijn vertaald; {stand.terugval} blok
                {stand.terugval === 1 ? "" : "ken"} tonen de Nederlandse tekst.
              </p>
              {stand.verouderd > 0 && (
                <p className="text-destructive">
                  {stand.verouderd} vertaling{stand.verouderd === 1 ? "" : "en"} zijn mogelijk verouderd: de
                  Nederlandse tekst is nadien gewijzigd.
                </p>
              )}
              <p className="text-muted-foreground">
                {stand.status === "gepubliceerd"
                  ? `Gepubliceerd op ${datum(stand.gepubliceerdOp)}.`
                  : "Nog niet gepubliceerd in deze taal."}
              </p>
            </div>
          )}

          {waarschuwingen.length > 0 && (
            <ul className="list-disc space-y-1 border-t pt-3 pl-5 text-sm text-destructive">
              {waarschuwingen.map((regel) => (
                <li key={regel}>{regel}</li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {heroIndex >= 0 && form.sections[heroIndex] && (
        <Card className="mb-6 border-2">
          <CardContent className="space-y-3 p-5">
            <h2 className="font-semibold">Hero bovenaan</h2>
            <Referentie taal={taal} sectie={bronSecties.get(form.sections[heroIndex].sleutel) ?? null} />
            <SectionFields
              section={form.sections[heroIndex]}
              index={heroIndex}
              update={update}
              alwaysAll
              voorstel={voorstelPerSectie.get(form.sections[heroIndex].sleutel) ?? null}
            />
          </CardContent>
        </Card>
      )}

      <Card className="mb-6">
        <CardContent className="space-y-3 p-5">
          <h2 className="font-semibold">Vindbaarheid</h2>
          {taal !== "nl" && bron && (
            <p className="rounded bg-muted p-3 text-xs text-muted-foreground">
              Nederlands: {bron.metaTitle} — {bron.metaDescription}
            </p>
          )}
          <div>
            <Label htmlFor="meta-title" className="text-xs">
              Titel in Google (max 60 tekens)
            </Label>
            <Input
              id="meta-title"
              value={form.metaTitle}
              maxLength={70}
              onChange={(event) => setForm({ ...form, metaTitle: event.target.value })}
            />
            {voorstel?.metaTitle && (
              <Voorstelregel
                tekst={voorstel.metaTitle}
                onOvernemen={() => setForm((prev) => ({ ...prev, metaTitle: voorstel.metaTitle }))}
              />
            )}
          </div>
          <div>
            <Label htmlFor="meta-description" className="text-xs">
              Omschrijving in Google (max 155 tekens)
            </Label>
            <Textarea
              id="meta-description"
              value={form.metaDescription}
              maxLength={200}
              onChange={(event) => setForm({ ...form, metaDescription: event.target.value })}
            />
            {voorstel?.metaDescription && (
              <Voorstelregel
                tekst={voorstel.metaDescription}
                onOvernemen={() => setForm((prev) => ({ ...prev, metaDescription: voorstel.metaDescription }))}
              />
            )}
          </div>
          <MediaPicker
            id="og-beeld"
            label="Deelbeeld voor sociale media"
            value={form.ogMediaId}
            onChange={(mediaId) => setForm({ ...form, ogMediaId: mediaId })}
          />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.indexeerbaar}
              onChange={(event) => setForm({ ...form, indexeerbaar: event.target.checked })}
            />
            Deze pagina mag in zoekmachines verschijnen
          </label>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {overige.map((section) => {
          const index = form.sections.indexOf(section);
          const sectieStand = stand?.secties.find((rij) => rij.sleutel === section.sleutel);
          return (
            <Card key={section.sleutel}>
              <CardContent className="space-y-3 p-5">
                <h2 className="flex flex-wrap items-center gap-2 font-semibold">
                  {section.sleutel}
                  {taal !== "nl" && sectieStand && !sectieStand.vertaald && (
                    <span className="rounded bg-muted px-2 py-0.5 text-xs font-normal">
                      Nog niet vertaald, toont Nederlands
                    </span>
                  )}
                  {taal !== "nl" && sectieStand?.verouderd && (
                    <span className="rounded bg-destructive/10 px-2 py-0.5 text-xs font-normal text-destructive">
                      Mogelijk verouderd — Nederlands gewijzigd op {datum(sectieStand.bronGewijzigdOp)}
                    </span>
                  )}
                </h2>
                <Referentie taal={taal} sectie={bronSecties.get(section.sleutel) ?? null} />
                <SectionFields
                  section={section}
                  index={index}
                  update={update}
                  alwaysAll={taal !== "nl"}
                  voorstel={voorstelPerSectie.get(section.sleutel) ?? null}
                />
              </CardContent>
            </Card>
          );
        })}
        <Button
          type="button"
          variant="outline"
          onClick={() =>
            setForm((prev) => {
              const nummer = prev.sections.filter((rij) => rij.type === "fotos").length + 1;
              return {
                ...prev,
                sections: [
                  ...prev.sections,
                  {
                    sleutel: `fotos-${nummer}`,
                    sortOrder: prev.sections.length,
                    type: "fotos",
                    overline: "",
                    titel: "",
                    tekst: "",
                    opsomming: [],
                    knopTekst: "",
                    knopLink: "",
                    knop2Tekst: "",
                    knop2Link: "",
                    mediaId: null,
                    alt: "",
                    media: [],
                  },
                ],
              };
            })
          }
        >
          Fotoblok toevoegen
        </Button>
      </div>


      <Card className="mt-8">
        <CardContent className="space-y-3 p-5">
          <h2 className="flex items-center gap-2 font-semibold">
            <History className="h-4 w-4" /> Versiegeschiedenis
          </h2>
          {(revisions.data ?? []).length === 0 && (
            <p className="text-sm text-muted-foreground">Er is nog geen eerdere versie bewaard.</p>
          )}
          {(revisions.data ?? []).map((revision) => (
            <div key={revision.id} className="flex flex-wrap items-center justify-between gap-2 border-t pt-3 text-sm">
              <span>
                {formatSiteDateTime(revision.createdAt)} — {revision.reden}
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={restore.isPending}
                onClick={() => {
                  if (window.confirm("Deze versie terugzetten als concept?")) restore.mutate(revision.id);
                }}
              >
                Terugzetten
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </main>
  );
}

function naarForm(page: {
  metaTitle: string;
  metaDescription: string;
  ogMediaId: string | null;
  indexeerbaar: boolean;
  sections: {
    id: string;
    sleutel: string;
    sortOrder: number;
    type: string;
    overline: string;
    titel: string;
    tekst: string;
    opsomming: string[];
    knopTekst: string;
    knopLink: string;
    knop2Tekst: string;
    knop2Link: string;
    mediaId: string | null;
    alt: string;
    media: SectionMedia[];
  }[];
}): FormState {
  return {
    metaTitle: page.metaTitle,
    metaDescription: page.metaDescription,
    ogMediaId: page.ogMediaId,
    indexeerbaar: page.indexeerbaar,
    sections: page.sections.map((section) => ({
      id: section.id,
      sleutel: section.sleutel,
      sortOrder: section.sortOrder,
      type: section.type,
      overline: section.overline,
      titel: section.titel,
      tekst: section.tekst,
      opsomming: section.opsomming,
      knopTekst: section.knopTekst,
      knopLink: section.knopLink,
      knop2Tekst: section.knop2Tekst,
      knop2Link: section.knop2Link,
      mediaId: section.mediaId,
      alt: section.alt,
      media: section.media,
    })),
  };
}

/** De Nederlandse tekst blijft zichtbaar als referentie boven de vertaalvelden. */
function Referentie({ taal, sectie }: { taal: Lang; sectie: { overline: string; titel: string; tekst: string; opsomming: string[] } | null }) {
  if (taal === "nl" || !sectie) return null;
  const regels = [sectie.overline, sectie.titel].filter((regel) => regel.trim() !== "");
  const lijst = sectie.opsomming.filter((regel) => regel.trim() !== "");
  if (regels.length === 0 && lijst.length === 0 && sectie.tekst.trim() === "") return null;
  return (
    <div className="rounded bg-muted p-3 text-xs leading-5 text-muted-foreground">
      <p className="mb-1 font-semibold">Nederlands</p>
      {regels.map((regel) => (
        <p key={regel.slice(0, 40)}>{regel}</p>
      ))}
      {sectie.tekst.trim() !== "" && <RichText waarde={sectie.tekst} className="mt-1" />}
      {lijst.map((regel) => (
        <p key={regel.slice(0, 40)}>{regel}</p>
      ))}
    </div>
  );
}

function Voorstelregel({ tekst, onOvernemen }: { tekst: string; onOvernemen: () => void }) {
  return (
    <div className="mt-2 flex flex-wrap items-start justify-between gap-2 rounded border border-dashed p-2 text-xs">
      <div className="flex-1 whitespace-pre-line">
        <RichText waarde={tekst} />
      </div>
      <Button type="button" size="sm" variant="outline" onClick={onOvernemen}>
        Overnemen
      </Button>
    </div>
  );
}

type FieldProps = {
  section: ContentSectionInput;
  index: number;
  update: (index: number, patch: Partial<ContentSectionInput>) => void;
  alwaysAll?: boolean;
  voorstel?: VertaalSectie | null;
};

function SectionFields({ section, index, update, alwaysAll = false, voorstel = null }: FieldProps) {
  const toon = (waarde: string) => alwaysAll || waarde.trim() !== "";
  return (
    <div className="space-y-3">
      {toon(section.overline) && (
        <div>
          <Label htmlFor={`overline-${index}`} className="text-xs">
            Bovenschrift
          </Label>
          <Input
            id={`overline-${index}`}
            value={section.overline}
            onChange={(event) => update(index, { overline: event.target.value })}
          />
          {voorstel?.overline && (
            <Voorstelregel tekst={voorstel.overline} onOvernemen={() => update(index, { overline: voorstel.overline })} />
          )}
        </div>
      )}
      {toon(section.titel) && (
        <div>
          <Label htmlFor={`titel-${index}`} className="text-xs">
            Titel
          </Label>
          <Input id={`titel-${index}`} value={section.titel} onChange={(event) => update(index, { titel: event.target.value })} />
          {voorstel?.titel && (
            <Voorstelregel tekst={voorstel.titel} onOvernemen={() => update(index, { titel: voorstel.titel })} />
          )}
        </div>
      )}
      {toon(section.tekst) && (
        <div>
          <Label id={`tekst-label-${index}`} htmlFor={`tekst-${index}`} className="text-xs">
            Tekst
          </Label>
          <RichTextEditor
            id={`tekst-${index}`}
            labelId={`tekst-label-${index}`}
            profiel={section.type === "hero" ? "kort" : "pagina"}
            value={section.tekst}
            onChange={(tekst) => update(index, { tekst })}
          />
          {voorstel?.tekst && (
            <Voorstelregel tekst={voorstel.tekst} onOvernemen={() => update(index, { tekst: voorstel.tekst })} />
          )}
        </div>
      )}
      {(alwaysAll || section.opsomming.length > 0) && (
        <div>
          <Label htmlFor={`lijst-${index}`} className="text-xs">
            Opsomming, één regel per punt
          </Label>
          <Textarea
            id={`lijst-${index}`}
            rows={4}
            value={section.opsomming.join("\n")}
            onChange={(event) => update(index, { opsomming: event.target.value.split("\n") })}
          />
          {voorstel && voorstel.opsomming.length > 0 && (
            <Voorstelregel
              tekst={voorstel.opsomming.join("\n")}
              onOvernemen={() => update(index, { opsomming: voorstel.opsomming })}
            />
          )}
        </div>
      )}
      {toon(section.knopTekst) && (
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor={`knop-${index}`} className="text-xs">
              Knoptekst
            </Label>
            <Input
              id={`knop-${index}`}
              value={section.knopTekst}
              onChange={(event) => update(index, { knopTekst: event.target.value })}
            />
            {voorstel?.knopTekst && (
              <Voorstelregel
                tekst={voorstel.knopTekst}
                onOvernemen={() => update(index, { knopTekst: voorstel.knopTekst })}
              />
            )}
          </div>
          <div>
            <Label htmlFor={`knoplink-${index}`} className="text-xs">
              Knoplink
            </Label>
            <Input
              id={`knoplink-${index}`}
              value={section.knopLink}
              onChange={(event) => update(index, { knopLink: event.target.value })}
            />
          </div>
        </div>
      )}
      {toon(section.knop2Tekst) && (
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor={`knop2-${index}`} className="text-xs">
              Tweede knoptekst
            </Label>
            <Input
              id={`knop2-${index}`}
              value={section.knop2Tekst}
              onChange={(event) => update(index, { knop2Tekst: event.target.value })}
            />
            {voorstel?.knop2Tekst && (
              <Voorstelregel
                tekst={voorstel.knop2Tekst}
                onOvernemen={() => update(index, { knop2Tekst: voorstel.knop2Tekst })}
              />
            )}
          </div>
          <div>
            <Label htmlFor={`knop2link-${index}`} className="text-xs">
              Tweede knoplink
            </Label>
            <Input
              id={`knop2link-${index}`}
              value={section.knop2Link}
              onChange={(event) => update(index, { knop2Link: event.target.value })}
            />
          </div>
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <MediaPicker
          id={`media-${index}`}
          label="Hoofdfoto van dit blok"
          value={section.mediaId}
          onChange={(mediaId) => update(index, { mediaId })}
        />
        <div>
          <Label htmlFor={`alt-${index}`} className="text-xs">
            Beschrijving van het beeld (verplicht bij een gekozen foto)
          </Label>
          <Input id={`alt-${index}`} value={section.alt} onChange={(event) => update(index, { alt: event.target.value })} />
          {voorstel?.alt && (
            <Voorstelregel tekst={voorstel.alt} onOvernemen={() => update(index, { alt: voorstel.alt })} />
          )}
        </div>
      </div>
      <FotoReeks section={section} index={index} update={update} />
    </div>
  );
}

/** Extra foto's bij één blok: toevoegen, ordenen, beschrijven en verwijderen. */
function FotoReeks({ section, index, update }: { section: ContentSectionInput; index: number; update: FieldProps["update"] }) {
  const media = section.media ?? [];

  const zet = (nieuw: SectionMedia[]) => update(index, { media: nieuw });

  const verplaats = (positie: number, richting: -1 | 1) => {
    const doel = positie + richting;
    if (doel < 0 || doel >= media.length) return;
    const kopie = [...media];
    const [rij] = kopie.splice(positie, 1);
    if (rij) kopie.splice(doel, 0, rij);
    zet(kopie);
  };

  return (
    <div className="space-y-3 rounded border border-dashed p-3">
      <p className="text-xs font-semibold">Extra foto's bij dit blok ({media.length})</p>
      {media.map((foto, positie) => (
        <div key={`${foto.mediaId}-${positie}`} className="grid gap-2 border-t pt-3 sm:grid-cols-[1fr_auto]">
          <div className="space-y-2">
            <MediaPicker
              id={`reeks-${index}-${positie}`}
              label={`Foto ${positie + 1}`}
              value={foto.mediaId}
              onChange={(mediaId) =>
                zet(media.map((rij, i) => (i === positie ? { ...rij, mediaId: mediaId ?? rij.mediaId } : rij)))
              }
            />
            <div>
              <Label htmlFor={`reeks-alt-${index}-${positie}`} className="text-xs">
                Beschrijving (verplicht)
              </Label>
              <Input
                id={`reeks-alt-${index}-${positie}`}
                value={foto.alt}
                onChange={(event) =>
                  zet(media.map((rij, i) => (i === positie ? { ...rij, alt: event.target.value } : rij)))
                }
              />
            </div>
            <div>
              <Label htmlFor={`reeks-caption-${index}-${positie}`} className="text-xs">
                Bijschrift (optioneel)
              </Label>
              <Input
                id={`reeks-caption-${index}-${positie}`}
                value={foto.caption}
                onChange={(event) =>
                  zet(media.map((rij, i) => (i === positie ? { ...rij, caption: event.target.value } : rij)))
                }
              />
            </div>
          </div>
          <div className="flex gap-2 sm:flex-col">
            <Button type="button" size="sm" variant="outline" aria-label={`Foto ${positie + 1} omhoog`} onClick={() => verplaats(positie, -1)}>
              Omhoog
            </Button>
            <Button type="button" size="sm" variant="outline" aria-label={`Foto ${positie + 1} omlaag`} onClick={() => verplaats(positie, 1)}>
              Omlaag
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              aria-label={`Foto ${positie + 1} verwijderen`}
              onClick={() => zet(media.filter((_, i) => i !== positie))}
            >
              Verwijderen
            </Button>
          </div>
        </div>
      ))}
      <AddFoto
        index={index}
        reedsGekozen={media.map((rij) => rij.mediaId)}
        onKies={(ids) => zet([...media, ...ids.map((mediaId) => ({ mediaId, url: null, alt: "", caption: "" }))])}
      />
    </div>
  );
}

/** Kiest één of meerdere foto's uit de bibliotheek en voegt ze onderaan de reeks toe. */
function AddFoto({
  onKies,
  index,
  reedsGekozen,
}: {
  onKies: (mediaIds: string[]) => void;
  index: number;
  reedsGekozen: string[];
}) {
  const [open, setOpen] = useState(false);
  const knop = useRef<HTMLButtonElement>(null);

  return (
    <div>
      <Button
        id={`reeks-nieuw-${index}`}
        ref={knop}
        type="button"
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
      >
        Foto's toevoegen aan de reeks
      </Button>
      <MediaDialog
        open={open}
        onOpenChange={(openState) => {
          setOpen(openState);
          if (!openState) knop.current?.focus();
        }}
        mode="multiple"
        reedsGekozen={reedsGekozen}
        onSelect={onKies}
      />
    </div>
  );
}
